import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { CapiProviderName } from "@next-platform/packages-ts-types";
import { useEffect } from "react";

import { type IAdvancedMatching } from "@/features/tracking/advancedMatching/AdvancedMatching";
import {
  FbqCommand,
  type IFbq,
  META_PIXEL_SCRIPT_ID,
  META_PIXEL_SCRIPT_SRC,
  META_PIXEL_SDK_VERSION,
  META_USER_DATA_KEY_EMAIL,
  META_USER_DATA_KEY_EXTERNAL_ID,
  MetaStandardEvent,
  type MetaWireStringPayload,
} from "@/features/tracking/providers/meta/metaPixel";
import { clearPendingConversionsForProvider, markConversionProviderReady } from "@/features/tracking/registry/conversionProviderRegistry";

const metaPixelLogger = appLogger.withTag("MetaPixel");

/**
 * Dataset ids that have already fired their initial `PageView` in this
 * page load. Module-scoped so StrictMode double-invoke, HMR remount,
 * React Suspense retries, or a parent re-render cannot cause duplicate
 * page views. Each dataset id appears at most once per full page load.
 */
const pageViewFiredForDatasetIdsKeys = new Set<string>();

function installFbqStubIfMissing(): void {
  if (window.fbq !== undefined) {
    return;
  }
  // Classic Meta Pixel bootstrap: installs a queuing stub that the
  // async-loaded fbevents.js replaces with the real implementation.
  // Adapted from Meta's official install snippet — the shape of the
  // stub (queue + callMethod swap) is what fbevents.js inspects on
  // load, so we cannot simplify it.
  const stub = function stubFn(...stubArgs: unknown[]): void {
    const callMethod = stub.callMethod;
    if (callMethod !== undefined) {
      callMethod.apply(stub, [stubArgs[0] as string, ...stubArgs.slice(1)]);
      return;
    }
    stub.queue.push(stubArgs);
  } as unknown as IFbq;
  stub.queue = [];
  stub.loaded = true;
  stub.version = META_PIXEL_SDK_VERSION;
  stub.push = stub;
  window.fbq = stub;
  if (window._fbq === undefined) {
    window._fbq = stub;
  }
}

function loadFbeventsScriptIfMissing(): void {
  if (document.getElementById(META_PIXEL_SCRIPT_ID) !== null) {
    return;
  }
  const script = document.createElement("script");
  script.id = META_PIXEL_SCRIPT_ID;
  script.async = true;
  script.src = META_PIXEL_SCRIPT_SRC;
  // Surface silent load failures (ad blockers, CSP violations,
  // network errors). Without this, fbevents.js never replaces the
  // stub's `callMethod`, every dispatched event accumulates in
  // `stub.queue` indefinitely, and Growth sees missing conversions
  // with no in-app signal. Info rather than warn: an ad blocker triggers
  // this on a large share of page loads, so at warn level it would drown
  // production logs and TrackJS telemetry.
  script.onerror = (): void => {
    metaPixelLogger.info("fbevents.js failed to load; conversions will be queued by the fbq stub but never flushed", {
      src: META_PIXEL_SCRIPT_SRC,
    });
  };
  document.head.appendChild(script);
}

function toMetaUserData(matching: IAdvancedMatching): MetaWireStringPayload {
  const result: MetaWireStringPayload = {};
  if (matching.email !== undefined) {
    result[META_USER_DATA_KEY_EMAIL] = matching.email;
  }
  if (matching.externalId !== undefined) {
    result[META_USER_DATA_KEY_EXTERNAL_ID] = matching.externalId;
  }
  return result;
}

/**
 * Mounts the Meta Pixel for every configured dataset and attaches
 * hashed Advanced Matching to each.
 *
 * Behaviour:
 * - No-op when `datasetIds` is empty (app not configured for Meta).
 *   Pending conversions for Meta are dropped and the ready latch is
 *   flipped so `fireConversion` calls pass through without queueing.
 * - Waits for `matching !== undefined` before initializing — pre-auth
 *   init with empty data poisons the session (Meta honors the first
 *   user_data it sees per pixel and silently ignores later updates).
 * - Issues `fbq('init', datasetId, userData)` for every dataset, in
 *   list order.
 * - Fires `PageView` exactly once per page load. `fbq('track', ...)`
 *   broadcasts to every initialized pixel, so one call reaches all
 *   datasets; firing once per dataset would produce duplicates in
 *   Events Manager.
 *
 * Idempotent against StrictMode double-invoke, HMR, component
 * remount, and any future sibling of `TrackingPixels` that imports
 * this hook.
 */
export function useMetaPixel({ datasetIds, matching }: { datasetIds: readonly string[]; matching: IAdvancedMatching | undefined }): void {
  // Join is stable as long as `datasetIds` is stable; a caller
  // passing a new array reference every render would defeat this, but
  // `TrackingPixels` resolves the list once per render from app
  // config so the reference churn is bounded.
  const datasetIdsKey = datasetIds.join(",");
  useEffect(() => {
    if (datasetIds.length === 0) {
      // Meta is not configured for this app. The provider is still
      // registered (side-effect import in `fireConversion.ts`), so
      // without this branch every `fireConversion` call would append
      // to the Meta pending queue forever — an unbounded memory
      // growth bug for apps that don't run Meta. Drop the queue and
      // flip the ready latch so future dispatches pass straight
      // through to `dispatchMetaConversion`, which no-ops when
      // `window.fbq` is undefined.
      clearPendingConversionsForProvider(CapiProviderName.Meta);
      markConversionProviderReady(CapiProviderName.Meta);
      return;
    }
    if (matching === undefined) {
      // Advanced Matching is still hashing. Keep queueing so that
      // any conversion fired in this window (e.g. a fast signup)
      // gets flushed the instant INIT completes below.
      return;
    }
    if (typeof window === "undefined") {
      return;
    }

    try {
      installFbqStubIfMissing();
      loadFbeventsScriptIfMissing();
    } catch (error: unknown) {
      metaPixelLogger.warn("Meta Pixel bootstrap failed; skipping dispatch", { error, datasetIds });
      return;
    }

    const metaPixelClient = window.fbq;
    if (metaPixelClient === undefined) {
      return;
    }

    const userData = toMetaUserData(matching);
    for (const datasetId of datasetIds) {
      metaPixelClient(FbqCommand.INIT, datasetId, userData);
    }

    // One PageView per page load — `fbq('track', ...)` broadcasts to
    // every initialized pixel, so a single call covers all datasets.
    // Guard keyed on the joined id list so identity changes (which
    // re-run this effect) don't re-fire PageView.
    if (!pageViewFiredForDatasetIdsKeys.has(datasetIdsKey)) {
      metaPixelClient(FbqCommand.TRACK, MetaStandardEvent.PAGE_VIEW);
      pageViewFiredForDatasetIdsKeys.add(datasetIdsKey);
    }

    // Flip the Meta provider's ready latch so the registry flushes
    // any conversions (e.g. Signup) queued while we were still
    // hashing Advanced Matching and waiting for this effect to run.
    // Idempotent: later identity changes re-run this effect but the
    // registry no-ops on already-flushed queues.
    markConversionProviderReady(CapiProviderName.Meta);
    // Deps are the joined dataset list (detects order changes and
    // add/remove), the presence flag for matching (catches the
    // undefined → `{}` logged-out transition where both digest
    // fields stay undefined), and the two primitive digests. A fresh
    // `{}` reference from `useAdvancedMatching` on every render
    // would otherwise re-fire the effect if `matching` itself were
    // in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above.
  }, [datasetIdsKey, matching === undefined, matching?.email, matching?.externalId]);
}

/**
 * Test-only reset for the page-load dedup guard. Exported with an
 * explicit name so production call sites can't invoke it by mistake.
 */
export function resetMetaPixelForTests(): void {
  pageViewFiredForDatasetIdsKeys.clear();
}
